import React from 'react';

const SearchLoader = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`${sizeClasses[size]} relative`}>
        {/* Animated search icon with pulsing effect */}
        <div className="absolute inset-0 animate-pulse">
          <svg
            className="w-full h-full text-primary-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        
        {/* Rotating ring around search icon */}
        <div className="absolute inset-0 animate-spin">
          <div className="w-full h-full rounded-full border-2 border-transparent border-t-primary-400 border-r-primary-300"></div>
        </div>
        
        {/* Inner pulsing dot */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-1 h-1 bg-primary-600 rounded-full animate-ping"></div>
        </div>
      </div>
    </div>
  );
};

// Professional search skeleton loader for search results
export const SearchResultsSkeleton = ({ rows = 5 }) => {
  return (
    <div className="bg-white rounded-lg border border-secondary-200">
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center space-x-3">
          <SearchLoader size="sm" />
          <div className="h-4 bg-secondary-200 rounded w-32 animate-pulse"></div>
          <div className="h-3 bg-secondary-100 rounded w-24 animate-pulse"></div>
        </div>
      </div>
      
      <div className="divide-y divide-secondary-200">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="p-6 animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-secondary-200 rounded-full"></div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
                <div className="h-3 bg-secondary-100 rounded w-1/2"></div>
              </div>
              <div className="flex space-x-2">
                <div className="w-16 h-8 bg-secondary-200 rounded"></div>
                <div className="w-16 h-8 bg-secondary-200 rounded"></div>
                <div className="w-16 h-8 bg-secondary-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Professional inline search loader for input fields
export const InlineSearchLoader = ({ className = '' }) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className="relative">
        <div className="w-4 h-4 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-4 h-4 border border-primary-300 rounded-full animate-ping opacity-75"></div>
      </div>
      <span className="text-xs text-primary-600 font-medium animate-pulse">Searching...</span>
    </div>
  );
};

// Professional search status indicator
export const SearchStatus = ({ isSearching, resultCount, searchTerm, className = '' }) => {
  if (isSearching) {
    return (
      <div className={`flex items-center space-x-2 text-sm text-primary-600 ${className}`}>
        <SearchLoader size="sm" />
        <span className="font-medium">Searching for "{searchTerm}"...</span>
      </div>
    );
  }

  if (searchTerm && resultCount !== undefined) {
    return (
      <div className={`flex items-center space-x-2 text-sm ${className}`}>
        <svg className="w-4 h-4 text-success-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        <span className="text-secondary-700">
          Found {resultCount} result{resultCount !== 1 ? 's' : ''} for "{searchTerm}"
        </span>
      </div>
    );
  }

  return null;
};

export default SearchLoader;